<template>
    <div>
        <NavbarOne />

        <div class="aa">
            <RoomPlacer v-if="placing" :product="placing.product" :photo-url="placing.photoUrl" @close="placing = null" @done="placed" />

        <div v-if="chat.state.enabled === false" class="aa__off">
                <h1 class="aa__off-title">The assistant is resting for the moment</h1>
                <p>Send us a photo of your room on WhatsApp and we will suggest pieces by hand.</p>
                <a :href="whatsappHref" target="_blank" rel="noopener" class="btn btn-solid mt-6" data-text="WhatsApp us"><span>WhatsApp us</span></a>
            </div>

            <div v-else class="aa__layout" :class="{ 'has-sidebar': sidebarOpen }">
                <!-- Past conversations -->
                <aside class="aa__sidebar">
                    <button type="button" class="aa__new" @click="newChat">
                        <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round"><path d="M12 5v14M5 12h14" /></svg>
                        New conversation
                    </button>
                    <p class="aa__side-title">{{ session.isAuthenticated.value ? 'Your conversations' : 'On this device' }}</p>
                    <p v-if="chat.state.loadingList" class="aa__side-empty">Loading…</p>
                    <p v-else-if="!chat.state.conversations.length" class="aa__side-empty">Nothing yet — your chats will be listed here.</p>
                    <ul v-else class="aa__list">
                        <li v-for="c in chat.state.conversations" :key="c.uuid">
                            <button type="button" class="aa__item" :class="{ 'is-active': c.uuid === chat.state.conversation }" @click="openConversation(c.uuid)">
                                <span class="aa__item-title">{{ c.title }}</span>
                                <span class="aa__item-meta">{{ relative(c.last_message_at) }} · {{ Math.ceil(c.message_count / 2) }} question{{ c.message_count > 2 ? 's' : '' }}</span>
                            </button>
                        </li>
                    </ul>
                    <p v-if="!session.isAuthenticated.value" class="aa__side-note">
                        <router-link to="/login">Sign in</router-link> to keep your conversations on every device.
                    </p>
                </aside>

                <!-- Thread -->
                <section class="aa__main">
                    <header class="aa__header">
                        <button type="button" class="aa__toggle" aria-label="Show conversations" @click="sidebarOpen = !sidebarOpen">
                            <svg viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round"><path d="M4 7h16M4 12h16M4 17h10" /></svg>
                        </button>
                        <div class="aa__avatar" aria-hidden="true">SH</div>
                        <div class="aa__heading">
                            <strong>Sweet Home assistant</strong>
                            <span>{{ chat.state.sending ? 'Thinking…' : 'Knows our collection, finishes and the way we work' }}</span>
                        </div>
                    </header>

                    <!-- The piece this chat is about (opened from its product page) -->
                    <div v-if="chat.focus.value" class="aa__focus">
                        <router-link :to="chat.focus.value.path" class="aa__focus-link">
                            <img v-if="chat.focus.value.image" :src="chat.focus.value.image" :alt="chat.focus.value.name" />
                            <span class="aa__focus-meta">
                                <em>Asking about</em>
                                <strong>{{ chat.focus.value.name }}</strong>
                                <span>{{ chat.focus.value.category ? `${chat.focus.value.category} · ` : '' }}from {{ formatPrice(chat.focus.value.price) }}</span>
                            </span>
                        </router-link>
                        <button type="button" class="aa__focus-room" :disabled="chat.locked.value || chat.photosLocked.value || chat.state.sending || chat.state.rendering" @click="chat.hasRoomPhoto.value ? startRender(chat.focus.value) : roomInput?.click()">
                            <svg viewBox="0 0 24 24" width="15" height="15" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M12 3l1.8 4.7 4.7 1.8-4.7 1.8L12 16l-1.8-4.7L5.5 9.5l4.7-1.8z" /></svg>
                            Show it in my room
                        </button>
                        <button type="button" class="aa__focus-clear" aria-label="Stop talking about this piece" title="Stop talking about this piece" @click="chat.setFocus(null)">
                            <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round"><path d="M6 6l12 12M18 6L6 18" /></svg>
                        </button>
                    </div>
                    <input ref="roomInput" type="file" accept="image/jpeg,image/png,image/webp" class="hidden" @change="roomPicked" />

                    <div ref="scroller" class="aa__body">
                        <div class="aa__thread">
                            <ChatThread
                                :messages="chat.state.messages"
                                :follow-ups="chat.lastFollowUps.value"
                                :sending="chat.state.sending"
                                :loading="chat.state.loadingHistory"
                                :error="chat.state.error"
                                :can-retry="Boolean(chat.state.lastFailed)"
                                :typing-label="typingLabel"
                                :limit="chat.limit.value"
                                :countdown="chat.limitCountdown.value"
                                :signed-in="session.isAuthenticated.value"
                                :renderable="chat.hasRoomPhoto.value && !chat.locked.value"
                                :rendering="chat.state.rendering"
                                @render="startRender"
                                @move="moveRender"
                                @refine="refineDesign"
                                @design-quote="quoteDesign"
                                @design-view="viewDesign"
                                @download="savePicture"
                                @draw-idea="drawIdea"
                                :locked="chat.locked.value"
                                :user="session.state.user"
                                :send-quote="(m, payload) => chat.sendQuote(m, payload).then((r) => { scrollToEnd(); return r; })"
                                @pick="(q) => onSend({ text: q, files: [] })"
                                @retry="chat.retry(route.fullPath).then(scrollToEnd)"
                                @new="newChat"
                            >
                                <template #empty>
                                    <div class="aa__empty">
                                        <div class="aa__empty-badge">SH</div>
                                        <template v-if="chat.focus.value">
                                            <h1 class="aa__empty-title">Hi{{ firstName ? ` ${firstName}` : '' }}, let's talk about the {{ chat.focus.value.name }}</h1>
                                            <p class="aa__empty-text">
                                                Add a photo of your room and I will show you this piece in it, tell you whether it suits the space — colours, size, style — or answer anything about it: finishes, sizes, a custom version, delivery and price.
                                            </p>
                                        </template>
                                        <template v-else>
                                            <h1 class="aa__empty-title">Hi{{ firstName ? ` ${firstName}` : '' }}, what are we furnishing?</h1>
                                            <p class="aa__empty-text">
                                                Talk to me like you would to someone in the showroom. Attach a photo of your room and I will tell you which pieces fit — colours, size, style — or ask about custom furniture, finishes, delivery and prices.
                                            </p>
                                        </template>
                                        <div v-if="!chat.locked.value" class="aa__starters">
                                            <button v-for="s in (chat.focus.value ? focusStarters : starters)" :key="s.title" type="button" class="aa__starter" @click="s.room ? roomInput?.click() : (s.photo ? composer?.focus() || pickPhoto() : (s.design ? composer?.setMode('draw') : (s.prefill ? (composer?.setMode('ask'), composer?.setText(s.prefill), composer?.focus()) : onSend({ text: s.text, files: [] }))))">
                                                <span class="aa__starter-icon" aria-hidden="true" v-html="s.icon"></span>
                                                <span class="aa__starter-body">
                                                    <strong>{{ s.title }}</strong>
                                                    <em>{{ s.hint }}</em>
                                                </span>
                                            </button>
                                        </div>
                                    </div>
                                </template>
                            </ChatThread>
                        </div>
                    </div>

                    <div class="aa__composer">
                        <ChatComposer
                            ref="composer"
                            :disabled="chat.state.sending || chat.state.loadingHistory || chat.locked.value"
                            :photos-disabled="chat.photosLocked.value"
                            :placeholder="chat.locked.value ? (chat.limitCountdown.value ? `Available again in ${chat.limitCountdown.value}` : 'The assistant is paused') : (chat.focus.value ? `Ask about the ${chat.focus.value.name}, or attach a photo of your room…` : 'Ask anything, attach a photo of your room, or paste a Pinterest link…')"
                            :hint="(chat.quotaHint.value ? chat.quotaHint.value + ' ' : '') + 'Answers are generated by AI and may be imperfect — our team reads every conversation and can confirm anything. Photos you send are kept with the conversation.'"
                            @send="onSend"
                                design-enabled
                                :design-disabled="chat.state.rendering"
                                :latest-design-version="chat.latestDesign.value?.payload?.design?.version || 0"
                                @design="onDesign"
                        />
                    </div>
                </section>
            </div>
        </div>
    </div>
</template>

<script setup>
import { computed, nextTick, onMounted, ref, watch } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import NavbarOne from '@/components/navbar/navbar-one.vue';
import ChatThread from '@/components/ai/chat-thread.vue';
import ChatComposer from '@/components/ai/chat-composer.vue';
import RoomPlacer from '@/components/ai/room-placer.vue';
import { useAiChat } from '@/composables/use-ai-chat';
import { drawIntent, refersToPhoto } from '@/lib/draw-intent';
import { useClientSession } from '@/composables/use-client-session';
import { whatsappLink } from '@/data/company';
import { formatPrice, storefrontRequest } from '@/lib/storefront-api';

const route = useRoute();
const router = useRouter();
const session = useClientSession();
const chat = useAiChat();
const placing = ref(null); // { product, photoUrl } while the 3D placement dialog is open

// Photo served through the API (with CORS) so the placement canvas may read it.
function roomPhotoApiUrl() {
  for (let i = chat.state.messages.length - 1; i >= 0; i -= 1) {
    const m = chat.state.messages[i];
    const index = (m.attachments || []).findIndex((a) => a.url && !a.local);
    if (m.role === 'user' && m.id && index >= 0) {
      return `${process.env.VUE_APP_STOREFRONT_API_ROOT || 'http://127.0.0.1:8000/api/storefront'}/ai/chat/${chat.state.conversation}/photo/${m.id}/${index}?visitor_id=${encodeURIComponent(chat.visitorId())}`;
    }
  }
  return '';
}

async function startRender(product) {
  if (product.model_glb_url && roomPhotoApiUrl()) {
    placing.value = { product, photoUrl: roomPhotoApiUrl() };
    return;
  }
  await chat.render({ product });
  await scrollToEnd();
}

async function onDesign({ text, fresh, files = [], imagesRole = 'base' }) {
  const latest = chat.latestDesign.value;
  await chat.design({ text, basedOn: fresh || !latest ? null : latest, files, imagesRole });
  await scrollToEnd();
}

async function drawIdea(message) {
  const draft = message.payload?.quote_draft;
  if (!draft) return;
  await chat.design({ text: [draft.title, draft.description].filter(Boolean).join('. ') });
  await scrollToEnd();
}

async function refineDesign({ message, text }) {
  await chat.design({ text, basedOn: message });
  await scrollToEnd();
}

async function viewDesign({ message, angle }) {
  await chat.designView(message, angle);
  await scrollToEnd();
}

async function savePicture({ message, index }) {
  const ok = await chat.downloadPicture(message, index);
  if (!ok) chat.setError('The picture opened in a new tab — use "Save image as…" if it did not download.');
}

async function quoteDesign(message) {
  await chat.designQuote(message);
  await scrollToEnd();
}

async function moveRender({ product, placement }) {
  await chat.render({ product, placement });
  await scrollToEnd();
}

async function placed({ composite, placement, error }) {
  const product = placing.value?.product;
  placing.value = null;
  if (!product) return;
  if (error) chat.setError(error); // falls back to the photo-only render below
  await chat.render({ product, composite: composite || null, placement });
  await scrollToEnd();
}

const scroller = ref(null);
const composer = ref(null);
const sidebarOpen = ref(false);

const whatsappHref = whatsappLink('Hello Sweet Home Gallery, I would like suggestions for my room.');
const firstName = computed(() => (session.state.user?.name || '').trim().split(/\s+/)[0] || '');
const typingLabel = computed(() => (chat.state.messages[chat.state.messages.length - 1]?.attachments?.length ? 'Looking at your photo…' : ''));

const starters = [
    {
        title: 'Draw my idea',
        hint: 'Describe a kitchen, a wardrobe, a sofa… and I make a picture',
        design: true,
        icon: '<svg viewBox="0 0 24 24" width="22" height="22" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"><path d="M12 3l1.8 4.7 4.7 1.8-4.7 1.8L12 16l-1.8-4.7L5.5 9.5l4.7-1.8z"/><path d="M5 19l1-2.5L8.5 15.5 6 16.5z"/></svg>',
    },
    {
        title: 'From Pinterest',
        hint: 'Paste a pin link — I read the picture and prepare the request',
        text: '',
        prefill: 'I want something like this: https://www.pinterest.com/pin/',
        icon: '<svg viewBox="0 0 24 24" width="22" height="22" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"><path d="M10 13a5 5 0 0 0 7.5.5l3-3a5 5 0 0 0-7-7l-1.5 1.5"/><path d="M14 11a5 5 0 0 0-7.5-.5l-3 3a5 5 0 0 0 7 7L12 19"/></svg>',
    },
    {
        title: 'Match my room',
        hint: 'Attach a photo and ask what would fit',
        photo: true,
        icon: '<svg viewBox="0 0 24 24" width="22" height="22" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"><rect x="3" y="5" width="18" height="14" rx="2"/><circle cx="9" cy="10" r="1.6"/><path d="M21 16l-5-5-7 7"/></svg>',
    },
    {
        title: 'Find a piece',
        hint: 'e.g. a dining table for 8 in walnut',
        text: 'I am looking for a dining table for 8 people, ideally in walnut. What do you have?',
        icon: '<svg viewBox="0 0 24 24" width="22" height="22" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"><circle cx="11" cy="11" r="7"/><path d="M20 20l-3.5-3.5"/></svg>',
    },
    {
        title: 'Custom furniture',
        hint: 'Made to your measurements and finish',
        text: 'Can you make a sofa to my own measurements, and how does that work?',
        icon: '<svg viewBox="0 0 24 24" width="22" height="22" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"><path d="M4 20l4-1 10-10-3-3L5 16l-1 4z"/><path d="M13 7l3 3"/></svg>',
    },
    {
        title: 'Delivery & payment',
        hint: 'Lead times, areas, how to pay',
        text: 'How long does delivery take and how can I pay?',
        icon: '<svg viewBox="0 0 24 24" width="22" height="22" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"><path d="M3 7h11v9H3zM14 10h4l3 3v3h-7z"/><circle cx="7" cy="18" r="1.6"/><circle cx="17" cy="18" r="1.6"/></svg>',
    },
];

const roomInput = ref(null);

const focusStarters = computed(() => {
    const name = chat.focus.value?.name || 'this piece';
    return [
        { title: 'Show it in my room', hint: 'Add a photo of your room — I place it there for you', room: true, icon: starters[2].icon },
        { title: 'Does it suit my room?', hint: 'Attach a photo and I judge colours, size and style', photo: true, icon: '<svg viewBox="0 0 24 24" width="22" height="22" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"><path d="M9 12l2 2 4-4"/><circle cx="12" cy="12" r="9"/></svg>' },
        { title: 'Sizes & finishes', hint: 'Dimensions, materials and the options we offer', text: `What are the dimensions of the ${name}, and which finishes or fabrics can I choose?`, icon: '<svg viewBox="0 0 24 24" width="22" height="22" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"><path d="M3 17l4-4 4 4 4-4 4 4"/><path d="M3 7h18"/><path d="M7 7v3M12 7v3M17 7v3"/></svg>' },
        { title: 'A custom version', hint: 'Another size, fabric or colour — made to order', text: `Can you make the ${name} in a different size, fabric or colour? What are my options and how does it work?`, icon: starters[4].icon },
    ];
});

// "Show me the X in my room" with a photo attached → draw it right after the assistant's answer.
function wantsInRoom(text) {
    return /\b(show|see|look|place|put|picture|imagine|visuali[sz]e|render|try|fit)\b[\s\S]*\b(room|space|here|there|photo|picture|it)\b|in my (room|living|bedroom|kitchen|office|space|salon|home)/i.test(text || '');
}

// The pinned piece + a photo of the room → one round: the assistant looks at the photo, then the picture is drawn.
async function roomPicked(event) {
    const file = event.target.files?.[0];
    event.target.value = '';
    const product = chat.focus.value;
    if (!file || !product) return;
    sidebarOpen.value = false;
    const ok = await chat.send({ text: `Here is my room — show me the ${product.name} in it.`, files: [file], page: route.fullPath });
    await scrollToEnd();
    if (ok) {
        chat.listConversations();
        if (!chat.locked.value) await startRender(product);
    }
}

async function scrollToEnd() {
    await nextTick();
    if (scroller.value) scroller.value.scrollTop = scroller.value.scrollHeight;
}

function pickPhoto() {
    document.querySelector('.aicc__attach input')?.click();
}

async function onSend({ text, files }) {
  // "Draw it" typed in Ask mode → straight to Draw mode and the picture, no chat round-trip.
  const intent = files.length ? { draw: false } : drawIntent(text);
  // With a catalog piece pinned, "show me a picture of it / how it looks in my room" means a room picture, not a drawing.
  if (intent.draw && chat.focus.value && (!intent.subject || refersToPhoto(text) || wantsInRoom(text))) {
    if (chat.hasRoomPhoto.value) {
      await startRender(chat.focus.value);
    } else {
      chat.setError(`Add a photo of your room first and I will show the ${chat.focus.value.name} in it.`);
      roomInput.value?.click();
    }
    return;
  }
  if (intent.draw) {
    const subject = intent.subject || chat.lastDescription();
    if (!subject) {
      chat.setError('Tell me what to draw — for example "draw a walk-in wardrobe in light oak with LED strips".');
      return;
    }
    composer.value?.setMode('draw');
    const latest = chat.latestDesign.value;
    const photo = !latest && refersToPhoto(text) ? chat.latestRoomPhoto() : null;
    await chat.design({ text: subject, basedOn: intent.subject && latest ? latest : null, photoMessage: photo });
    await scrollToEnd();
    return;
  }
    sidebarOpen.value = false;
    await scrollToEnd();
    const focus = chat.focus.value;
    const ok = await chat.send({ text, files, page: route.fullPath });
    await scrollToEnd();
    if (ok) chat.listConversations();
    nextTick(() => composer.value?.focus());
    // The pinned piece + a room photo + "show it in my room" → draw it straight away.
    if (ok && focus && (files.length || chat.hasRoomPhoto.value) && wantsInRoom(text) && !chat.locked.value && !chat.state.rendering) {
        await startRender(focus);
        return;
    }
  // The assistant understood a request for a picture in other words → draw what it described.
  const last = chat.state.messages[chat.state.messages.length - 1];
  const prompt = last?.role === 'assistant' && last.payload?.draw_prompt;
  if (prompt && !chat.locked.value) {
    composer.value?.setMode('draw');
    const photo = last.payload.draw_from_photo ? chat.latestRoomPhoto() : null;
    await chat.design({ text: prompt, photoMessage: photo });
    await scrollToEnd();
  }
}

function newChat() {
    chat.startNew();
    sidebarOpen.value = false;
    nextTick(() => composer.value?.focus());
}

async function openConversation(uuid) {
    sidebarOpen.value = false;
    await chat.openConversation(uuid);
    await scrollToEnd();
}

function relative(value) {
    if (!value) return '';
    const diff = (Date.now() - new Date(value).getTime()) / 1000;
    if (diff < 60) return 'just now';
    if (diff < 3600) return `${Math.floor(diff / 60)} min ago`;
    if (diff < 86400) return `${Math.floor(diff / 3600)} h ago`;
    if (diff < 7 * 86400) return `${Math.floor(diff / 86400)} d ago`;
    return new Date(value).toLocaleDateString();
}

// "/assistant?product=12" (from a product page): a fresh chat with that piece pinned.
async function focusFromQuery() {
    const id = Number(route.query.product);
    if (!id) return false;
    router.replace({ path: route.path }); // a reload should not start yet another conversation
    try {
        const response = await storefrontRequest(`/products/${id}`);
        const item = response.data;
        if (!item) return false;
        const base = Number(item.base_price || 0);
        const sale = item.sale_price !== null && item.sale_price !== undefined && item.sale_price !== '' ? Number(item.sale_price) : null;
        chat.startNew({
            focus: {
                id: item.id,
                name: item.name,
                category: item.category?.name || '',
                price: sale !== null && sale >= 0 && sale < base ? sale : base,
                image: item.main_image_url || item.gallery_image_urls?.[0] || '',
                path: `/product-details/${item.id}`,
                model_glb_url: item.has_ar ? item.model_glb_url || null : null,
            },
        });
        return true;
    } catch (e) {
        return false;
    }
}

onMounted(async () => {
    session.initializeSession?.().catch(() => null);
    await chat.ensureStatus();
    if (!chat.enabled.value) return;
    const pinned = await focusFromQuery();
    if (!pinned && !chat.state.messages.length && chat.storedConversation()) await chat.loadHistory();
    chat.listConversations();
    await scrollToEnd();
    nextTick(() => composer.value?.focus());
});

watch(() => chat.state.sending, (sending) => { if (sending) scrollToEnd(); });
watch(() => chat.state.limit, (limit) => { if (limit) scrollToEnd(); });
watch(() => session.state.token, (token, previous) => {
    if (token === previous) return;
    if (chat.state.conversation) chat.loadHistory();
    chat.listConversations();
});
</script>

<style>
.aa { background: #faf8f5; border-top: 1px solid #e8e9ea; }
.aa__off { max-width: 640px; margin: 0 auto; padding: 90px 20px; text-align: center; color: #3c474e; }
.aa__off-title { font-size: 26px; font-weight: 600; color: #172430; margin-bottom: 8px; }

.aa__layout { display: grid; grid-template-columns: 280px minmax(0, 1fr); grid-template-rows: minmax(0, 1fr); height: calc(100vh - 84px); min-height: 560px; max-width: 1320px; margin: 0 auto; }

.aa__focus { display: flex; align-items: center; gap: 10px; padding: 8px 16px; background: #fff; border-bottom: 1px solid #e8e9ea; }
.aa__focus-link { display: flex; align-items: center; gap: 10px; min-width: 0; flex: 1; color: inherit; text-decoration: none; }
.aa__focus-link img { flex: 0 0 44px; width: 44px; height: 44px; border-radius: 8px; object-fit: cover; background: #f3efe9; }
.aa__focus-meta { display: flex; flex-direction: column; min-width: 0; line-height: 1.25; }
.aa__focus-meta em { font-style: normal; font-size: 11px; text-transform: uppercase; letter-spacing: 0.06em; color: #8a9197; }
.aa__focus-meta strong { font-size: 14px; color: #172430; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
.aa__focus-meta > span { font-size: 12px; color: #8a9197; }
.aa__focus-link:hover strong { color: #bb976d; }
.aa__focus-room { display: inline-flex; align-items: center; gap: 6px; height: 36px; padding: 0 14px; border-radius: 999px; border: 1px solid #bb976d; background: #bb976d; color: #fff; font-size: 13px; font-weight: 600; cursor: pointer; white-space: nowrap; }
.aa__focus-room:hover { background: #172430; border-color: #172430; }
.aa__focus-room:disabled { opacity: 0.5; cursor: default; }
.aa__focus-clear { flex: 0 0 32px; height: 32px; border-radius: 8px; border: 0; background: transparent; color: #8a9197; display: inline-flex; align-items: center; justify-content: center; cursor: pointer; }
.aa__focus-clear:hover { background: #faf8f5; color: #172430; }
.dark .aa__focus { background: #22313d; border-color: #3c474e; }
.dark .aa__focus-meta strong { color: #fff; }
@media (max-width: 640px) {
  .aa__focus { padding: 8px 10px; }
  .aa__focus-room { padding: 0 10px; font-size: 12px; }
}

.aa__sidebar { min-height: 0; border-right: 1px solid #e8e9ea; padding: 18px 14px; overflow-y: auto; background: #fff; display: flex; flex-direction: column; }
.aa__new { display: flex; align-items: center; justify-content: center; gap: 8px; width: 100%; height: 44px; border-radius: 12px; border: 1px solid #172430; background: #172430; color: #fff; font-weight: 600; font-size: 14px; cursor: pointer; }
.aa__new:hover { background: #bb976d; border-color: #bb976d; }
.aa__side-title { margin: 20px 6px 8px; font-size: 11px; font-weight: 600; letter-spacing: 0.08em; text-transform: uppercase; color: #8a9197; }
.aa__side-empty { margin: 0 6px; font-size: 13px; color: #8a9197; line-height: 1.5; }
.aa__list { list-style: none; margin: 0; padding: 0; display: flex; flex-direction: column; gap: 2px; }
.aa__item { display: flex; flex-direction: column; width: 100%; text-align: left; padding: 9px 10px; border-radius: 10px; border: 0; background: transparent; cursor: pointer; color: #172430; }
.aa__item:hover { background: #faf8f5; }
.aa__item.is-active { background: rgba(187, 151, 109, 0.14); }
.aa__item-title { font-size: 13.5px; font-weight: 500; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
.aa__item-meta { font-size: 11.5px; color: #8a9197; margin-top: 2px; }
.aa__side-note { margin: auto 6px 0; padding-top: 16px; font-size: 12px; color: #8a9197; line-height: 1.5; }
.aa__side-note a { color: #bb976d; text-decoration: underline; }

.aa__main { display: flex; flex-direction: column; min-width: 0; min-height: 0; }
.aa__header { display: flex; align-items: center; gap: 12px; padding: 12px 20px; border-bottom: 1px solid #e8e9ea; background: #fff; }
.aa__toggle { display: none; width: 36px; height: 36px; border-radius: 8px; border: 1px solid #e8e9ea; background: #fff; color: #172430; align-items: center; justify-content: center; cursor: pointer; }
.aa__avatar { flex: 0 0 38px; height: 38px; border-radius: 999px; background: #bb976d; color: #fff; display: flex; align-items: center; justify-content: center; font-size: 12px; font-weight: 700; letter-spacing: 0.05em; }
.aa__heading { display: flex; flex-direction: column; line-height: 1.25; min-width: 0; }
.aa__heading strong { font-size: 16px; color: #172430; }
.aa__heading span { font-size: 12.5px; color: #8a9197; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }

.aa__body { flex: 1; min-height: 0; overflow-y: auto; padding: 28px 20px; }
.aa__thread { max-width: 820px; margin: 0 auto; }
.aa__thread .aic__row { max-width: 80%; }
.aa__thread .aic__bubble { font-size: 15px; padding: 12px 16px; }
.aa__thread .aic__products { grid-template-columns: repeat(auto-fill, minmax(240px, 1fr)); }
.aa__thread .aic__photo img { width: 220px; height: 160px; }

.aa__empty { text-align: center; padding: 24px 8px 8px; }
.aa__empty-badge { width: 56px; height: 56px; margin: 0 auto 18px; border-radius: 999px; background: #bb976d; color: #fff; display: flex; align-items: center; justify-content: center; font-weight: 700; letter-spacing: 0.05em; }
.aa__empty-title { font-size: 28px; font-weight: 600; color: #172430; margin: 0 0 10px; }
.aa__empty-text { max-width: 560px; margin: 0 auto; font-size: 15px; line-height: 1.6; color: #3c474e; }
.aa__starters { display: grid; grid-template-columns: repeat(2, minmax(0, 1fr)); gap: 10px; max-width: 640px; margin: 28px auto 0; }
.aa__starter { display: flex; align-items: flex-start; gap: 12px; padding: 14px; border-radius: 14px; border: 1px solid #e8e9ea; background: #fff; text-align: left; cursor: pointer; transition: border-color 0.15s ease, transform 0.15s ease; }
.aa__starter:hover { border-color: #bb976d; transform: translateY(-1px); }
.aa__starter-icon { flex: 0 0 38px; height: 38px; border-radius: 10px; background: rgba(187, 151, 109, 0.14); color: #bb976d; display: flex; align-items: center; justify-content: center; }
.aa__starter-body { display: flex; flex-direction: column; gap: 2px; min-width: 0; }
.aa__starter-body strong { font-size: 14px; color: #172430; }
.aa__starter-body em { font-style: normal; font-size: 12.5px; color: #8a9197; line-height: 1.4; }

.aa__composer { background: #fff; }
.aa__composer .aicc { max-width: 860px; margin: 0 auto; border-top: 0; padding: 12px 20px 14px; }
.aa__main > .aa__composer { border-top: 1px solid #e8e9ea; }

@media (max-width: 900px) {
  .aa__layout { grid-template-columns: minmax(0, 1fr); height: calc(100vh - 70px); position: relative; }
  .aa__sidebar { position: absolute; inset: 0 auto 0 0; width: min(300px, 85vw); z-index: 5; transform: translateX(-100%); transition: transform 0.2s ease; box-shadow: 0 0 40px rgba(23, 36, 48, 0.2); }
  .aa__layout.has-sidebar .aa__sidebar { transform: translateX(0); }
  .aa__toggle { display: inline-flex; }
  .aa__body { padding: 18px 12px; }
  .aa__thread .aic__row { max-width: 90%; }
  .aa__empty-title { font-size: 22px; }
  .aa__starters { grid-template-columns: 1fr; }
}

.dark .aa { background: #172430; border-color: #3c474e; }
.dark .aa__sidebar, .dark .aa__header, .dark .aa__composer, .dark .aa__starter, .dark .aa__toggle { background: #1e2a35; border-color: #3c474e; }
.dark .aa__item, .dark .aa__heading strong, .dark .aa__empty-title, .dark .aa__starter-body strong, .dark .aa__off-title, .dark .aa__toggle { color: #fff; }
.dark .aa__item:hover { background: #22313d; }
.dark .aa__empty-text, .dark .aa__off { color: #cfd4d8; }
.dark .aa__main > .aa__composer { border-color: #3c474e; }
</style>
